import cn from 'classnames';
import { CSSProperties, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CountdownTimer from 'src/components/common/CountdownTimer';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { CategoryPageContext } from 'src/constructors/pageConstructors/CategoryPageConstructor';
import { formatCurrency, scrollToTop } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { MediaManager } from 'src/manager/MediaManager';
import { getRaces } from 'src/services/raceApi';
import { setSelectedCategory } from 'src/store/app/actions';
import { RaceInstance, RaceWidgetMediaProps } from 'src/types/common-types/raceWidget';
import { RootState } from 'src/types/store-types';
import './styles.scss';

export const RaceWidget: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const categoryPage = useContext(CategoryPageContext);
  const { sideBarMenuIsOpen } = useSelector((state: RootState) => state.app);
  const [instances, setInstances] = useState<RaceInstance[]>([]);

  const goToRace = useCallback(
    (race: RaceInstance) => {
      window.__gdlib?.notify?.('GD_GAMES_CATEGORY_ACTION', race?.category ?? '');
      _setSelectedCategory('race');
      navigate('/category/race');
      scrollToTop();
    },
    [navigate, _setSelectedCategory]
  );

  const templateIds = useMemo(() => {
    const raceTemplateIds = categoryPage?.pageFields?.raceTemplateIds ?? [];
    const ids: number[] = [];
    for (const item of raceTemplateIds) {
      const value = typeof item === 'object' && item !== null ? item.value : item;
      if (value == null) continue;
      const num = Number(value);
      if (!Number.isNaN(num)) ids.push(num);
    }
    return ids;
  }, [categoryPage?.pageFields?.raceTemplateIds]);

  const filteredInstances = useMemo(() => {
    if (templateIds.length === 0) return [];
    return instances.filter((item) => templateIds.includes(Number(item?.template_id)));
  }, [instances, templateIds]);

  useEffect(() => {
    if (templateIds.length === 0) return;
    scrollToTop();
    void getRaces()
      .then((res) => {
        const data = res?.result?.races;
        const active = Array.isArray(data)
          ? data.filter((r: RaceInstance) => r.status === 'inProgress' && r.isActive)
          : [];
        setInstances(active);
      })
      .catch((error) => {
        console.error('Failed to load race instances', error);
      });
  }, [templateIds]);

  if (!filteredInstances.length) return null;

  return (
    <div className="raceWidget">
      {filteredInstances.map((item) => {
        const headers = item.prizeStructure?.headers ?? [];
        const rewardedPositions = headers.reduce((sum, h) => sum + (h.place_to - h.place_from + 1), 0);
        const prizeFormatted = item.prize_pool == null ? '-' : formatCurrency(Number(item.prize_pool), item.currency);

        return (
          <div
            key={item.id ?? item.name}
            className={cn('raceWidget__card', {
              raceWidget__card__sidebar_open: sideBarMenuIsOpen,
            })}
            onClick={() => goToRace(item)}
            style={
              {
                ['--race-bg-desktop']: `url(/images/raceWidget-bg.svg)`,
                ['--race-bg-mobile']: `url(/images/raceWidget-bg-mobile.svg)`,
              } as CSSProperties
            }
          >
            <div className="raceWidget__cardInner">
              {isDesktop && <RaceWidgetMedia imgUrl={item.img_url} name={item.name} />}

              <div className="raceWidget__content">
                <div className="raceWidget__badges">
                  {item.status === 'inProgress' && (
                    <span className="raceWidget__badge raceWidget__badge--ongoing">
                      <span className="raceWidget__dot" />
                      {t('ongoing')}
                    </span>
                  )}
                  <span className="raceWidget__id">ID:{item.id}</span>
                </div>

                <div className="raceWidget__title">
                  {isMobile && <RaceWidgetMedia imgUrl={item.img_url} name={item.name} />}
                  {item.name}
                </div>

                <div className="raceWidget__meta">
                  <div className="raceWidget__meta_area">
                    <div className="raceWidget__metaRow">
                      <span className="raceWidget__metaLabel">
                        {t('rewardable')} {t('position')}:
                      </span>
                      <span className="raceWidget__metaValue">{rewardedPositions}</span>
                    </div>
                  </div>
                  <div className="raceWidget__prizeBox">
                    <span className="raceWidget__prizeLabel">{t('prize')}</span>
                    <span className="raceWidget__prizeValue">{prizeFormatted}</span>
                  </div>
                </div>

                {item.expire_time ? (
                  <CountdownTimer
                    classname="raceWidget__countdown"
                    targetDate={item.expire_time}
                    view="names"
                    longNames
                    size="sm"
                    bold
                  />
                ) : null}
              </div>

              <div className="raceWidget__prize">
                <button
                  className="raceWidget__playButton"
                  type="button"
                  aria-label="Go to race"
                  onClick={(event) => {
                    event.stopPropagation();
                    goToRace(item);
                  }}
                >
                  {isDesktop ? (
                    <SvgIcon src={icons.play} size="lg" color="white" />
                  ) : (
                    <>
                      <span>{t('goTorace')}</span>
                      <SvgIcon src={icons.arrowRight} size="sm" />
                    </>
                  )}
                </button>
                <div className="raceWidget__icon">
                  <SvgIcon src={MediaManager.getSrcFromMediaAssets('race-trophy.svg', '/navigation-icons/')} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RaceWidget;

const RaceWidgetMedia: FC<RaceWidgetMediaProps> = ({ imgUrl, name }) => {
  return useMemo(
    () => (
      <div className={cn('raceWidget__media', { 'raceWidget__media--has-image': !!imgUrl })}>
        <img
          className="raceWidget__mediaImage"
          src={imgUrl || MediaManager.getSrcFromMediaAssets('race-widget-fallback.png', '/')}
          alt={name}
        />
      </div>
    ),
    [imgUrl, name]
  );
};
